import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import EditCommissionPayment from './EditCommissionPayment';
import AddCommissionPaymentDialog from './AddCommissionPaymentDialog';
import FileUploadModal from './FileUploadModal';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { fetchPaymentModeSelect, fetchPaymentStatusSelect } from '../../../services/common';
import { formatDate, formatCurrencyNoDecimals, bookingTokens, cardSx, cardHeadSx, tableHeadSx, emptyStateSx, primaryButtonSx } from '../bms_helper';

const CommissionPayments = ({
  booking,
  onActionComplete,
  paymentModes: propPaymentModes,
  paymentStatuses: propPaymentStatuses
}) => {
  const { bookingCommissionPayments, fetchBookingCommissionPayments, deleteBookingCommissionPayment, addBookingCommissionPayment, attachProof } = useBookingDetails();
  const { openConfirmModal, openSuccessModal, openErrorModal } = useModal();
  const [editingPayment, setEditingPayment] = useState(null);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [paymentModes, setPaymentModes] = useState(propPaymentModes || []);
  const [paymentStatuses, setPaymentStatuses] = useState(propPaymentStatuses || []);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedPaymentForProof, setSelectedPaymentForProof] = useState(null);

  // Fetch commission payments when component mounts
  useEffect(() => {
    if (booking?.id) {
      fetchBookingCommissionPayments(booking.id);
    }
  }, [booking?.id, fetchBookingCommissionPayments]);

  // Fetch payment modes and statuses if not provided as props
  useEffect(() => {
    const fetchOptions = async () => {
      if (propPaymentModes && propPaymentStatuses) {
        setPaymentModes(propPaymentModes);
        setPaymentStatuses(propPaymentStatuses);
        return;
      }

      setLoadingOptions(true);
      try {
        const [modes, statuses] = await Promise.all([
          fetchPaymentModeSelect(),
          fetchPaymentStatusSelect()
        ]);
        setPaymentModes(modes || []);
        setPaymentStatuses(statuses || []);
      } catch (error) {
        console.error('Error fetching payment options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [propPaymentModes, propPaymentStatuses]);
  
  const handleEditPayment = (payment, commission) => {
    setEditingPayment(payment);

  };

  const handleAddPayment = () => {
    setAddPaymentDialogOpen(true);
  };

  const handleAddPaymentClose = async (result) => {
    setAddPaymentDialogOpen(false);
    if (result === 'added' || result === 'updated') {
      // Refresh the payments data
      if (booking?.id) {
        await fetchBookingCommissionPayments(booking.id);
      }
      if (onActionComplete) {
        onActionComplete();
      }
    }
  };

  const handleDeletePayment = async (payment, commission) => {
    openConfirmModal(
      'Delete Payment',
      'Are you sure you want to delete this payment? This action cannot be undone.',
      async () => {
        try {
          await deleteBookingCommissionPayment(
            booking.id,
            payment.id
          );

          // Show success message
          openSuccessModal(
            'Payment Deleted',
            'The payment has been successfully deleted.'
          );

          // Refresh the payments data
          if (booking?.id) {
            await fetchBookingCommissionPayments(booking.id);
          }

        } catch (error) {
          openErrorModal(
            'Delete Failed',
            error.message || 'Failed to delete the payment. Please try again.'
          );
        }
      },
      'Delete'  // confirmLabel
    );
  };

  const handlePaymentDialogClose = async (result) => {
    if (result === 'deleted' || result === 'updated') {
      // Refresh the payments data
      if (booking?.id) {
        await fetchBookingCommissionPayments(booking.id);
      }
    }
    setEditingPayment(null);
  };

  const handleAttachProof = (payment) => {
    setSelectedPaymentForProof(payment);
    setUploadModalOpen(true);
  };

  const handleUploadProof = async (file) => {
    try {
      await attachProof(booking.id, 'commission', selectedPaymentForProof.id, file);
      openSuccessModal('Success', 'Commission payment proof uploaded successfully');
    } catch (error) {
      openErrorModal('Error', error.message || 'Failed to upload commission payment proof');
      throw error;
    }
  };

  return (
    <>
    {loadingOptions ? (
      <Paper sx={{ width: '100%', p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading payment options...
        </Typography>
      </Paper>
    ) : (
      <Paper elevation={0} sx={{ ...cardSx, width: '100%', overflow: 'hidden' }}>
        <Box sx={cardHeadSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: 14 }}>✅</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink }}>Commission Received</Typography>
          </Box>
          {booking.status !== 'cancelled' && booking.status !== 'draft' && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddPayment} sx={primaryButtonSx}>Add Payment</Button>
          )}
        </Box>
      <TableContainer>
        <Table size="small" sx={{ '& thead tr': { background: '#f8fbf9' }, '& th': { padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.55px', color: bookingTokens.subtle, borderBottom: `1.5px solid ${bookingTokens.border}` } }}>
          <TableHead>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookingCommissionPayments && bookingCommissionPayments.length > 0 ? (
              bookingCommissionPayments.map((payment) => {
                return (
                  <TableRow key={payment.id} sx={{ '&:hover td': { bgcolor: '#f2f8f4' } }}>
                    <TableCell sx={{ fontWeight: 700, color: bookingTokens.green }}>{formatCurrencyNoDecimals(payment.amount)}</TableCell>
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell><Box component="span" sx={{ bgcolor: bookingTokens.bg, border: `1px solid ${bookingTokens.border}`, fontSize: 11.5, fontWeight: 600, px: 1.125, py: 0.375, borderRadius: 1 }}>{payment.payment_method_display || payment.payment_method || '-'}</Box></TableCell>
                    <TableCell sx={{ color: bookingTokens.muted }}>{payment.reference_number || '-'}</TableCell>
                    <TableCell><Box component="span" sx={{ bgcolor: bookingTokens.greenLt, color: bookingTokens.green, fontSize: 10.5, fontWeight: 700, px: 1.125, py: 0.25, borderRadius: 99 }}>Received</Box></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {payment.payment_proof ? (
                          <Button
                            size="small"
                            variant="text"
                            color="primary"
                            href={payment.payment_proof}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            View
                          </Button>
                        ) : (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleAttachProof(payment)}
                            sx={{ p: 0.5 }}
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePayment(payment)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 0, border: 'none' }}>
                  <Box sx={emptyStateSx}>
                    <Typography sx={{ fontSize: 40, opacity: 0.4 }}>💰</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: bookingTokens.muted }}>No payments available.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>
    )}

    <EditCommissionPayment
      open={!!editingPayment}
      onClose={handlePaymentDialogClose}
      booking={booking}
      payment={editingPayment}
      paymentModes={paymentModes}
      paymentStatuses={paymentStatuses}
    />
    
    <AddCommissionPaymentDialog
      open={addPaymentDialogOpen}
      onClose={handleAddPaymentClose}
      booking={booking}
      paymentModes={paymentModes}
      paymentStatuses={paymentStatuses}
    />

    <FileUploadModal
      open={uploadModalOpen}
      onClose={() => setUploadModalOpen(false)}
      onUpload={handleUploadProof}
      title="Upload Commission Payment Proof"
    />
    </>
  );
};

export default CommissionPayments;
